import {
  IconProps,
  InputGroup,
  InputGroupProps,
  InputProps,
  InputRightElement,
} from '@chakra-ui/react';
import {
  ChangeEventHandler,
  forwardRef,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useDebounce } from 'react-use';
import { convertPxToRems } from '~/utils-ts';

import IconButton from '../IconButton';
import Input from '../Input';
import { SearchIcon } from '../icons';
import {
  searchInputFocusStyles,
  searchInputHoverStyles,
} from './SearchInput.styles';
import { CrossIcon } from './components/CrossIcon';

export interface ISearchInputProps {
  defaultValue?: string;
  delay?: number;
  inputGroupProps?: InputGroupProps;
  inputProps?: Omit<InputProps, 'onChange' | 'defaultValue' | 'value'>;
  iconProps?: IconProps;
  minLength?: number;
  onChange: (value?: string) => void;
  trackId?: string;
}

export const SearchInput = forwardRef<HTMLInputElement, ISearchInputProps>(
  (
    {
      defaultValue,
      delay = 500,
      inputGroupProps,
      inputProps,
      iconProps,
      onChange,
      trackId,
    },
    ref,
  ) => {
    const [value, setValue] = useState(defaultValue ?? '');

    const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
      if (event.target.value !== value) {
        setValue(event.target.value);
      }
    };

    const cleanValue = useCallback(() => {
      const searchValue = '';
      onChange(searchValue);
      setValue(searchValue);
    }, [onChange]);

    const hasValue = !!value;

    useEffect(() => {
      if (!defaultValue) {
        setValue('');
      } else {
        setValue(defaultValue);
      }
    }, [defaultValue]);

    useDebounce(
      () => {
        if (defaultValue !== value) {
          onChange(value);
        }
      },
      delay,
      [value],
    );

    return (
      <InputGroup {...inputGroupProps}>
        <Input
          onChange={handleChange}
          borderColor="primary.100"
          fontSize="md"
          lineHeight={convertPxToRems(14)}
          placeholder="Search by keyword"
          value={value}
          paddingRight={10}
          _hover={searchInputHoverStyles}
          _focus={searchInputFocusStyles}
          {...inputProps}
          ref={ref}
          track-id={trackId}
        />
        <InputRightElement>
          <IconButton
            onClick={cleanValue}
            rounded="full"
            _hover={{
              backgroundColor: hasValue ? 'primary.50' : 'transparent',
              rounded: 'full',
            }}
            width={6}
            height={6}
            variant="ghost"
            aria-label="Clear"
            icon={
              hasValue ? (
                <CrossIcon color="primary.500" width={4} height={4} />
              ) : (
                <SearchIcon
                  color="primary.500"
                  width={4}
                  height={4}
                  aria-label="search-icon"
                  {...iconProps}
                />
              )
            }
          />
        </InputRightElement>
      </InputGroup>
    );
  },
);
